import { useState } from 'react';
import { useIsMobile } from '../../../hooks/use-mobile';
import type { ZoomPan } from '../board-grid/useZoomPan';

export interface PhotoExpansion {
  /** Whether the picture can be opened out here at all — see below. */
  offered: boolean;
  /** Whether it is, right now. */
  expanded: boolean;
  toggle: () => void;
}

/**
 * Opening a landscape picture out to the height of a phone's screen.
 *
 * A wall shot sideways and drawn at the width of a phone is a strip a third of
 * the screen tall: the holds are a few pixels across and the rest of the screen
 * is form. Pinching in helps one hold at a time and loses the wall around it.
 * Opened out, the picture's frame takes every bit of height the first fields do
 * not need (`editorExpandedHeight`), the photo fills it, and the sides that no
 * longer fit are dragged across — the same drag as a zoomed picture, because it
 * *is* one, resting at the scale that covers the frame (see `useZoomPan`).
 *
 * **Offered only on a phone, and only for a landscape picture.** A portrait one
 * is already as tall as the screen allows (`photo-height.ts` caps it), and on a
 * desktop a landscape one is drawn wide enough to work on as it is.
 *
 * Kept by the page rather than by the picture, so it outlives the climb: "set
 * another one" keeps the photo, and a setter who opened the wall out for the
 * last problem is still drawing on the same wall.
 *
 * **Side by side it is always open, and not offered** (`split`): the picture's
 * column there is the screen's height already (see `ClimbEditorSplit`), and the
 * picture fills it whatever its shape. What the setter chose upright is kept for
 * when the phone is turned back.
 *
 * `aspect` is the picture's width over its height, or nothing while it loads.
 */
export function useExpandablePhoto(
  aspect: number | null | undefined,
  zoom: ZoomPan,
  split = false
): PhotoExpansion {
  const isMobile = useIsMobile();
  const [wanted, setWanted] = useState(false);

  const offeredUpright = isMobile && !!aspect && aspect > 1;
  const offered = !split && offeredUpright;
  const expanded = split || (offeredUpright && wanted);

  const toggle = () => {
    if (split) return;
    setWanted(!expanded);
    // Either way the picture lands at rest in its new frame: covering it opened
    // out, whole folded back. A zoom made in the other frame is a framing of a
    // picture that is no longer on screen.
    zoom.reset();
    // Opened out, the picture and the fields are sized to the screen together —
    // which only holds from the top of the page, where the picture starts.
    if (!expanded) window.scrollTo({ top: 0 });
  };

  return { offered, expanded, toggle };
}
